import { z } from "zod";

import {
  isDefensePosition,
  type DefensePosition,
} from "../../shared/domain/defense";
import {
  isPlayoffBestOf,
  isPlayoffSeriesStatus,
  type PlayoffBestOf,
  type PlayoffSeriesStatus,
} from "../../shared/domain/playoffSeries";
import {
  isScheduleField,
  isScheduleStatus,
  isScheduleTimeSlot,
  isSpecialBeerDutyKind,
  type BeerDuty,
  type ScheduleField,
  type ScheduleStatus,
  type ScheduleTimeSlot,
  type SpecialBeerDutyKind,
} from "../../shared/domain/schedule";
import { InvalidDataError } from "../shared/errors";
import type {
  ClaimModel,
  EventModel,
  GameModel,
  InviteModel,
  MemberModel,
  PlayerModel,
  PlayoffSeriesModel,
  PregameModel,
  PresenceModel,
  RemoteTimestamp,
  TeamModel,
  UserDirectoryModel,
  ScheduleModel,
  SeasonModel,
} from "../shared/types/contracts";

const identifier = z.string().min(1);
const nullableIdentifier = identifier.nullable();
const finiteNumber = z.number().finite();
const nonNegativeInteger = z.number().int().nonnegative();
const timestamp = z.custom<RemoteTimestamp>(
  (value) =>
    typeof value === "number" ||
    value instanceof Date ||
    (typeof value === "object" &&
      value !== null &&
      "toMillis" in value &&
      typeof value.toMillis === "function") ||
    (typeof value === "object" &&
      value !== null &&
      (("seconds" in value &&
        typeof value.seconds === "number" &&
        "nanoseconds" in value &&
        typeof value.nanoseconds === "number") ||
        ("_seconds" in value &&
          typeof value._seconds === "number" &&
          "_nanoseconds" in value &&
          typeof value._nanoseconds === "number"))),
);
const auditFields = {
  createdAt: timestamp.optional(),
  createdBy: identifier.optional(),
  updatedAt: timestamp.optional(),
  updatedBy: identifier.optional(),
};
const statsSchema = z.object({
  AB: finiteNumber.default(0),
  H: finiteNumber.default(0),
  "1B": finiteNumber.default(0),
  "2B": finiteNumber.default(0),
  "3B": finiteNumber.default(0),
  HR: finiteNumber.default(0),
  BB: finiteNumber.default(0),
  K: finiteNumber.default(0),
  RBI: finiteNumber.default(0),
  R: finiteNumber.default(0),
  SF: finiteNumber.default(0),
});
const defenseInningSchema = z.object({
  assignment: z.record(z.string(), identifier).default({}),
  sitters: z.array(identifier).default([]),
  oop: z.array(identifier).default([]),
});

const defensePositionSchema = z.custom<DefensePosition>(isDefensePosition);
const scheduleFieldSchema = z.custom<ScheduleField>(isScheduleField);
const timeSlotSchema = z.custom<ScheduleTimeSlot>(isScheduleTimeSlot);
const scheduleStatusSchema = z.custom<ScheduleStatus>(isScheduleStatus);
const playoffBestOfSchema = z.custom<PlayoffBestOf>(isPlayoffBestOf);
const playoffSeriesStatusSchema = z.custom<PlayoffSeriesStatus>(
  isPlayoffSeriesStatus,
);
const beerDutySchema: z.ZodType<BeerDuty> = z.union([
  z.object({
    kind: z.custom<SpecialBeerDutyKind>(isSpecialBeerDutyKind),
  }),
  z.object({
    kind: z.literal("player"),
    playerId: identifier,
    nameSnapshot: z.string().min(1),
  }),
]);

const teamSchema = z
  .object({
    id: identifier,
    schemaVersion: nonNegativeInteger.default(1),
    name: z.string().min(1),
    activeGameId: nullableIdentifier.default(null),
    activeSeasonId: nullableIdentifier.default(null),
    jerseyHolderPlayerId: nullableIdentifier.default(null),
    ...auditFields,
  })
  .passthrough();

const playerSchema = z
  .object({
    id: identifier,
    name: z.string().min(1),
    number: finiteNumber.nullable().default(null),
    positionPreferences: z.array(defensePositionSchema).default([]),
    active: z.boolean().default(true),
    revision: nonNegativeInteger.default(0),
    ...auditFields,
  })
  .passthrough();

const pregameSchema = z
  .object({
    id: identifier,
    seasonId: nullableIdentifier.default(null),
    opponentName: z.string().default(""),
    isHome: z.boolean().default(false),
    openInning: z.boolean().default(false),
    activePlayerIds: z.array(identifier).default([]),
    lineupPlayerIds: z.array(identifier).default([]),
    gamePitcherId: nullableIdentifier.default(null),
    defense: z.array(defenseInningSchema).nullable().default(null),
    selectedScheduleId: nullableIdentifier.default(null),
    scheduledDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .default(null),
    timeSlot: timeSlotSchema.nullable().default(null),
    field: scheduleFieldSchema.nullable().default(null),
    beerDuty: beerDutySchema.nullable().default(null),
    revision: nonNegativeInteger.default(0),
    ...auditFields,
  })
  .passthrough();

const lineupEntrySchema = z.object({
  playerId: nullableIdentifier,
  name: z.string().min(1),
  number: finiteNumber.nullable().default(null),
});

const gameStateSchema = z.object({
  inning: z.number().int().positive().default(1),
  batting: z.enum(["us", "them"]).default("us"),
  outs: z.number().int().min(0).max(3).default(0),
  batterIndex: nonNegativeInteger.default(0),
  bases: z
    .tuple([nullableIdentifier, nullableIdentifier, nullableIdentifier])
    .default([null, null, null]),
  ourRunsByInning: z.array(nonNegativeInteger).default([0]),
  opponentRunsByInning: z.array(nonNegativeInteger).default([0]),
  statsByPlayerId: z.record(z.string(), statsSchema).default({}),
  lastBatterId: nullableIdentifier.default(null),
  final: z.boolean().default(false),
  mercyRuleWinner: z.enum(["us", "them"]).optional(),
});

const gameSchema = z
  .object({
    id: identifier,
    schemaVersion: nonNegativeInteger.default(1),
    status: z.enum(["in_progress", "final", "archived", "discarded"]),
    revision: nonNegativeInteger.default(0),
    seasonId: nullableIdentifier.default(null),
    opponentName: z.string().default(""),
    isHome: z.boolean().default(false),
    scheduleId: nullableIdentifier.default(null),
    scheduledDate: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/)
      .nullable()
      .default(null),
    timeSlot: timeSlotSchema.nullable().default(null),
    field: scheduleFieldSchema.nullable().default(null),
    beerDuty: beerDutySchema.nullable().default(null),
    playoffSeriesId: nullableIdentifier.default(null),
    seriesGameNumber: z.number().int().positive().nullable().default(null),
    openInning: z.boolean().default(false),
    lineup: z.array(lineupEntrySchema),
    defense: z.array(defenseInningSchema).default([]),
    state: gameStateSchema,
    undoStack: z.array(identifier).default([]),
    scorerUid: nullableIdentifier.default(null),
    scorerNameSnapshot: z.string().nullable().default(null),
    forfeitWinner: z.enum(["us", "them"]).optional(),
    legacyStatsByName: z.record(z.string(), statsSchema).optional(),
    startingJerseyHolderPlayerId: nullableIdentifier.optional(),
    archivedAt: timestamp.optional(),
    ...auditFields,
  })
  .passthrough();

const scheduleSchema = z
  .object({
    id: identifier,
    seasonId: nullableIdentifier.default(null),
    revision: nonNegativeInteger.default(0),
    status: scheduleStatusSchema,
    gameId: nullableIdentifier.default(null),
    scheduledDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    timeSlot: timeSlotSchema,
    field: scheduleFieldSchema,
    opponentName: z.string().min(1),
    isHome: z.boolean(),
    beerDuty: beerDutySchema,
    playoffSeriesId: nullableIdentifier.default(null),
    seriesGameNumber: z.number().int().positive().nullable().default(null),
    ...auditFields,
  })
  .passthrough();

const playoffSeriesSchema = z
  .object({
    id: identifier,
    seasonId: nullableIdentifier.default(null),
    revision: nonNegativeInteger.default(0),
    status: playoffSeriesStatusSchema,
    title: z.string().min(1),
    opponentName: z.string().min(1),
    bestOf: playoffBestOfSchema,
    scheduleIds: z.array(identifier),
    ourWins: nonNegativeInteger.default(0),
    opponentWins: nonNegativeInteger.default(0),
    winner: z.enum(["us", "them"]).nullable().default(null),
    ...auditFields,
  })
  .passthrough();

const seasonSchema = z
  .object({
    id: identifier,
    year: z.number().int().min(1900).max(2200).nullable().default(null),
    label: z.string().min(1),
    status: z.enum(["active", "closed"]),
    revision: nonNegativeInteger.default(0),
    startedAt: timestamp.optional(),
    startedBy: identifier.optional(),
    closedAt: timestamp.optional(),
    closedBy: identifier.optional(),
    ...auditFields,
  })
  .passthrough();

const memberSchema = z
  .object({
    uid: identifier,
    role: z.literal("editor"),
    claimedPlayerId: nullableIdentifier.default(null),
    joinedViaInviteVersion: nonNegativeInteger.default(1),
    authProviders: z.array(z.string()).default([]),
    joinedAt: timestamp.optional(),
  })
  .passthrough();

const directorySchema = z
  .object({
    uid: identifier,
    teamIds: z.array(identifier).default([]),
    defaultTeamId: nullableIdentifier.default(null),
    updatedAt: timestamp.optional(),
  })
  .passthrough();

const inviteSchema = z
  .object({
    id: identifier,
    token: z.string().min(1).nullable().default(null),
    tokenHash: z.string().min(1).optional(),
    version: nonNegativeInteger.nullable().default(null),
    active: z.boolean().default(false),
    createdAt: timestamp.optional(),
    createdBy: identifier.optional(),
  })
  .passthrough();

const claimSchema = z
  .object({
    id: identifier,
    uid: identifier,
    claimedAt: timestamp.optional(),
  })
  .passthrough();

const presenceSchema = z
  .object({
    id: identifier,
    state: z.enum(["online", "offline"]),
    lastChanged: timestamp,
    connectionId: nullableIdentifier.default(null),
    claimedPlayerId: nullableIdentifier.default(null),
    claimedPlayerNameSnapshot: z.string().nullable().default(null),
  })
  .passthrough();

const eventSchema = z
  .object({
    id: identifier,
    type: z.string().min(1),
    category: z.string().min(1),
    gameId: nullableIdentifier.default(null),
    baseRevision: nonNegativeInteger.nullable().default(null),
    committedRevision: nonNegativeInteger.nullable().default(null),
    actorUid: identifier,
    actorPlayerId: nullableIdentifier.default(null),
    actorNameSnapshot: z.string().min(1),
    occurredAt: timestamp,
    summary: z.string(),
    payload: z.unknown().default(null),
    inversePatch: z.unknown().default(null),
    undoTargetEventId: nullableIdentifier.default(null),
  })
  .passthrough();

function decode<T>(source: string, schema: z.ZodType<T>, value: unknown): T {
  const result = schema.safeParse(value);
  if (!result.success) {
    throw new InvalidDataError(source, { cause: result.error });
  }
  return result.data;
}

export const decodeTeam = (value: unknown): TeamModel =>
  decode("team document", teamSchema, value) as TeamModel;

export const decodePlayer = (value: unknown): PlayerModel =>
  decode("player document", playerSchema, value) as PlayerModel;

export const decodePregame = (value: unknown): PregameModel =>
  decode("pregame document", pregameSchema, value) as PregameModel;

export const decodeGame = (value: unknown): GameModel =>
  decode("game document", gameSchema, value) as GameModel;

export const decodeSchedule = (value: unknown): ScheduleModel =>
  decode("schedule document", scheduleSchema, value) as ScheduleModel;

export const decodePlayoffSeries = (value: unknown): PlayoffSeriesModel =>
  decode(
    "playoff series document",
    playoffSeriesSchema,
    value,
  ) as PlayoffSeriesModel;

export const decodeSeason = (value: unknown): SeasonModel =>
  decode("season document", seasonSchema, value) as SeasonModel;

export const decodeMember = (value: unknown): MemberModel =>
  decode("member document", memberSchema, value) as MemberModel;

export const decodeUserDirectory = (value: unknown): UserDirectoryModel =>
  decode(
    "user directory document",
    directorySchema,
    value,
  ) as UserDirectoryModel;

export const decodeInvite = (value: unknown): InviteModel =>
  decode("invite document", inviteSchema, value) as InviteModel;

export const decodeClaim = (value: unknown): ClaimModel =>
  decode("claim document", claimSchema, value) as ClaimModel;

export const decodePresence = (value: unknown): PresenceModel =>
  decode("presence document", presenceSchema, value) as PresenceModel;

export const decodeEvent = (value: unknown): EventModel =>
  decode("event document", eventSchema, value) as EventModel;

export const teamCollectionDecoders = {
  claims: decodeClaim,
  players: decodePlayer,
} satisfies Record<string, (value: unknown) => ClaimModel | PlayerModel>;
